import { Loader } from '@fukku/loader'
import {
	type ExpandibleElement,
	ListExpandible,
} from 'fukku/ListExpandible/ListExpandible'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { SearchLocationForm } from 'maps/components/commons/SearchLocationForm/SearchLocationForm'
import { StoreAddress } from 'maps/components/commons/StoreAddress/StoreAddress'
import { StoreInfo } from 'maps/components/commons/StoreInfo/StoreInfo'
import { StoreListError } from 'maps/components/commons/StoreListError/StoreListError'
import { useExpandedStore } from 'maps/hooks/useExpandedStore/useExpandedStore'
import { useMapState } from 'maps/hooks/useMapState/useMapState'
import type { Store } from 'maps/types'
import { isDroppointType, isStoresType } from 'maps/utils/mapTypes/mapTypes'
import { cx } from 'utils/cx'

import styles from './StoreList.module.scss'

interface StoreListProps {
	readonly className?: string
	readonly title?: string
	readonly titleClassName?: string
	readonly showSizes?: boolean
	readonly showPhone?: boolean
	readonly showDisclaimer?: boolean
	readonly onViewMap?: () => void
	readonly onConfirm?: (store: Store) => void
}

export function StoreList({
	className,
	title,
	titleClassName,
	showSizes,
	showPhone,
	showDisclaimer,
	onViewMap,
	onConfirm,
}: StoreListProps): React.ReactNode {
	const { t } = useLabels()
	const {
		stores = [],
		type,
		onModal,
		loading,
		hasDistanceLimit,
		filteredStores = [],
	} = useMapState()
	const { expanded, setExpanded } = useExpandedStore()

	const handleExpand = (storeId: Store['id']): void => {
		const store = filteredStores.find((s) => s.id === storeId) ?? undefined
		setExpanded(store)
	}

	const accessibilityLoaderText = {
		loading: t('accessibility.storeLocator.card.loading.text'),
		loaded: t('accessibility.storeLocator.card.loaded.text'),
	}

	const isStoreOrDroppoint = isStoresType(type) || isDroppointType(type)
	const showSearchAddress = loading || isStoreOrDroppoint || stores.length > 0

	const hasFilteredStores = !loading && filteredStores.length > 0

	const storeList =
		filteredStores?.map((store) => {
			const { id } = store
			const ariaIds = {
				descriptionId: `store-${id}-description`,
				extendedInfoId: `store-${id}-extended-info`,
			}
			return {
				title: <StoreAddress showSizes={showSizes} store={store} />,
				description: (
					<StoreInfo
						store={store}
						showPhone={showPhone}
						showDisclaimer={showDisclaimer}
						onViewMap={onViewMap}
						onConfirm={onConfirm}
						isExpanded={expanded?.id === store.id}
						ariaIds={ariaIds}
					/>
				),
				id: `store-${id}`,
				value: id,
				expanded: expanded?.id === store.id,
			} satisfies ExpandibleElement
		}) ?? []

	return (
		<div
			className={cx(styles.wrapper, onModal && styles.modalWrapper, className)}
		>
			{title && (
				<p id='search-title' className={titleClassName}>
					{title}
				</p>
			)}
			{showSearchAddress && <SearchLocationForm />}
			<Loader loading={loading} accessibilityText={accessibilityLoaderText} />
			{hasFilteredStores && (
				<div className={styles.list}>
					<ListExpandible
						name='store-list'
						options={storeList}
						onExpand={handleExpand}
						srText={t('accessibility.storeLocator.card.expand.link')}
						isFreeContent
						data-testid='maps.storeList'
					/>
					{hasDistanceLimit && (
						<p className={styles.text}>
							{t('storeLocator.searchBox.searchRadio.text')}
						</p>
					)}
				</div>
			)}
			<StoreListError />
		</div>
	)
}
